import { put, call, takeLatest, select } from "redux-saga/effects";
import * as types from "../constants/actionTypes";
import { logAPI } from "../api/andyliticsApi";
import { ambulatorySurveyRequest } from "../api/epicApi";
import * as portal from "../api/portalApi";
import * as instaDocBFFAPI from "../api/instaDocBFFApi";
import * as limbo from "../api/limboApi";
import * as Tools from "../helpers/Tools";
import * as ssoSaga from "./ssoSaga.js";

export default function* videoVisitSaga() {
    yield takeLatest(types.INSTADOC_PROVIDER_INFO_START, handleProviderInfo);
    yield takeLatest(types.IS_MOBILE_NUMBER, handleIsMobileNumber);
    yield takeLatest(types.SMS_VIDEO_VISIT_LINK, handleSmsVideoVisitLink);
    yield takeLatest(types.EMAIL_VIDEO_VISIT_LINK, handleEmailVideoVisitLink);
    yield takeLatest(types.SMS_VIDEO_VISIT_LINK_FOR_GUEST, handleSmsVideoVisitLinkForGuest);
    yield takeLatest(types.EMAIL_VIDEO_VISIT_LINK_FOR_GUEST, handleEmailVideoVisitLinkForGuest);
    yield takeLatest(types.CANCEL_VIDEO_VISIT_LINK, handleCancelVideoVisitLink);
    yield takeLatest(types.VIDEO_VISIT_SHARE_LINK_HISTORY, handleShareLinkHistory);
    yield takeLatest(types.AMBULATORY_VIDEO_VISIT_SURVEY_REQUEST, handleVideoVisitSurvey);
    yield takeLatest([types.GUEST_LINK_APPROVAL_STATUS], guestLinkApprovalStatus);
    yield takeLatest([types.ALLOW_GUEST], handleGuestAllowDeny);
    yield takeLatest([types.DENY_GUEST], handleGuestAllowDeny);
    yield takeLatest([types.APPOINTMENT_HISTORY], handleAppointmentHistory);
    yield takeLatest([types.CREATE_VIRTUAL_ROOM], handleCreateVirtualRoom);
}

export function* handleProviderInfo(data) {
    const start = new Date();
    try {
        const portal = yield select((state) => state.portal);
        let employeeTypes = portal.emplyeeTypes;
        if (!employeeTypes) {
            employeeTypes = yield getEmployeeTypes();
        }

        const domain = yield select((state) => state.domain);
        let token = domain.idt;
        let sdcAllowed = false;
        const instadocToken = yield ssoSaga.FetchInstadocToken();
        if (instadocToken && instadocToken.result) {
            token = instadocToken.result.tokenString;
            const adGroups = instadocToken.result.adGroups;
            const sdcGroups = window.serverData.data.sameDayGroups.split(";");
            if (adGroups) {
                for (let i = 0; i < sdcGroups.length; i++) {
                    const sdcGroup = sdcGroups[i].trim().toLowerCase();
                    for (let j = 0; j < adGroups.length; j++) {
                        if (adGroups[j].toLowerCase() === sdcGroup) {
                            sdcAllowed = true;
                            break;
                        }
                    }
                }
            }
        }

        const lanId = data.lanId || domain.dlan;
        const providerInfo = yield Tools.safeCall(instaDocBFFAPI.getProviderInfo, token, lanId);
        const { err, result } = providerInfo;
        if (!err) {
            const departments = result && result.departments && result.departments.filter(d => d.providerInDeptStatus !== 'DELETED' && d.status !== 'DELETED');
            let employeeRole = null;
            if (result && result.providerType) {
                employeeRole = yield getEmployeeRole(result.providerType);
            }

            yield put({
                type: types.INSTADOC_SET_EMPLOYEE, employeeType: result && result.providerType,
                employeeRole, epicId: result && result.providerID, providerExternalName: result && result.providerExternalName ? result.providerExternalName : null
            });

            if (!employeeRole) {
                const phyndData = yield ssoSaga.LoadPhyndData(lanId);
            }

            const providerEpicId = (result && result.providerID) || (domain && domain.eid);
            let providerEpicIds = [];

            if (employeeRole !== 'staff') {
                for (let i = 0; i < departments.length; i++) {
                    const depId = departments[i].id;
                    yield Tools.safeCall(instaDocBFFAPI.subscribeForSync, domain.idt, depId);
                    departments[i].selected = true;
                    departments[i].displayName = departments[i].name;
                    providerEpicIds = providerEpicIds.concat(departments[i].genericProviders.map(p => ({ providerExternalId: p.id, departmentInternalId: depId })));
                }
                if (providerEpicId) {
                    providerEpicIds.push({ providerExternalId: providerEpicId });
                }
            }

            departments.unshift({ displayName: 'All Departments', isAll: true, selected: true });
            yield put({ type: types.SET_PROVIDER_EPIC_IDS, providerEpicIds, sdcAllowed });
            yield put({ type: types.INSTADOC_PROVIDER_INFO_SUCCESS, providerInfo: departments, sdcAllowed });
            logAPI("Saga.VideoVisit.GetProviderInfo.Success", start, null, departments);
        }
        else {
            yield put({ type: types.INSTADOC_PROVIDER_INFO_FAILURE, error: err });
            logAPI("Saga.VideoVisit.GetProviderInfo.Failure", start, null, err);
        }
    }
    catch (ex) {
        yield put({ type: types.INSTADOC_PROVIDER_INFO_FAILURE });
        logAPI("Saga.VideoVisit.GetProviderInfo.Failure", start, null, ex);
    }

    if (data.callback) {
        data.callback();
    }
}

function* getEmployeeRole(employeeType) {
    if (!employeeType) {
        return null;
    }

    const portal = yield select((state) => state.portal);
    const { staffs, providers } = portal.employeeTypes;
    employeeType = employeeType.toLowerCase();

    if (staffs) {
        for (let i = 0; i < staffs.length; i++) {
            if (employeeType === staffs[i].toLowerCase()) {
                return 'staff';
            }
        }
    }

    if (providers) {
        for (let i = 0; i < providers.length; i++) {
            if (employeeType === providers[i].toLowerCase()) {
                return 'provider';
            }
        }
    }

    return null;
}

export function* getEmployeeTypes() {
    const start = new Date();
    let employeeTypes = null;
    try {
        const response = yield call(portal.getEmployeeTypes);
        const { roles, statuses } = response;
        const statusDictionay = {};
        for (let i = 0; i < statuses.length; i++) {
            statusDictionay[statuses[i].key] = statuses[i].name;
        }
        yield put({ type: types.ROOM_STATUSES, statuses: statusDictionay });
        if (roles) {
            const { providers, staffs } = roles;
            employeeTypes = { providers, staffs, error: null };
            yield put({ type: types.EMPLOYEE_TYPES, data: employeeTypes });
            logAPI("Saga.VideoVisit.storeEmployeeTypes.Success", start, null, { providers: providers && providers.length, staffs: staffs && staffs.length });
        }
    }
    catch (error) {
        yield put({ type: types.EMPLOYEE_TYPES, data: { providers: null, staffs: null, error } });
        logAPI("Saga.VideoVisit.storeEmployeeTypes.Failure", start, null, JSON.stringify(error));
    }
    return employeeTypes;
}

export function* handleIsMobileNumber(data) {
    try {
        if (data.waiting) {
            const domain = yield select(state => state.domain);
            const response = yield call(limbo.isMobileNumber, domain.idt, data.phoneNumber);
            yield put({ type: types.IS_MOBILE_NUMBER, ...response, waiting: false, error: null });
            logAPI('Saga.VideoVist.IsMobileNumber.Success', new Date(), response);
        }
    }
    catch (error) {
        yield put({ type: types.IS_MOBILE_NUMBER, error, waiting: false });
        logAPI('Saga.VideoVist.IsMobileNumber.Failure', new Date(), error);
    }
    finally {
        if (data.callback) {
            const isMobileNumber = yield select(state => state.videoVisit.isMobileNumber);
            data.callback(isMobileNumber);
        }
    }
}

export function* handleSmsVideoVisitLink(payload) {
    const start = new Date();
    const domain = yield select((state) => state.domain);
    try {
        if (domain.t) {
            const response = yield call(portal.smsVideoVisitLink, domain.t, payload.room, payload.mobileNumber, payload.participantName);
            if (response && !response.hasError && !response.errorCode) {
                logAPI("Saga.VideoVisit.SMSVisitLink.Success", start, null, response);
                yield put({ type: types.SMS_VIDEO_VISIT_LINK_SUCCESS, response });
            }
            else {
                yield put({ type: types.SMS_VIDEO_VISIT_LINK_FAILURE, response });
                logAPI("Saga.VideoVisit.SMSVisitLink.Failure", start, null, { error: response && response.error, room: payload.room, phone: payload.mobileNumber });
            }
        } else {
            yield put({ type: types.SMS_VIDEO_VISIT_LINK_FAILURE });
            logAPI("Saga.VideoVisit.SMSVisitLink.Failure", start, null);
        }
    } catch (error) {
        yield put({ type: types.SMS_VIDEO_VISIT_LINK_FAILURE, error });
        logAPI("Saga.VideoVisit.SMSVisitLink.Failure", start, null, JSON.stringify(error));
    }
    if (payload.callback) {
        payload.callback();
    }
}

export function* handleEmailVideoVisitLink(payload) {
    const start = new Date();
    const domain = yield select((state) => state.domain);
    try {
        if (domain.t) {
            const response = yield call(portal.emailVideoVisitLink, domain.t, payload.room, payload.email, payload.participantName);
            if (response && !response.hasError && !response.errorCode) {
                logAPI("Saga.VideoVisit.EmailVisitLink.Success", start, null, response);
                yield put({ type: types.EMAIL_VIDEO_VISIT_LINK_SUCCESS, response });
            }
            else {
                yield put({ type: types.EMAIL_VIDEO_VISIT_LINK_FAILURE, response });
                logAPI("Saga.VideoVisit.EmailVisitLink.Failure", start, null, { error: response && response.error, room: payload.room, email: payload.email });
            }
        } else {
            yield put({ type: types.EMAIL_VIDEO_VISIT_LINK_FAILURE });
            logAPI("Saga.VideoVisit.EmailVisitLink.Failure", start, null);
        }
    } catch (error) {
        yield put({ type: types.EMAIL_VIDEO_VISIT_LINK_FAILURE, error });
        logAPI("Saga.VideoVisit.EmailVisitLink.Failure", start, null, JSON.stringify(error));
    }
    if (payload.callback) {
        payload.callback();
    }
}

export function* handleSmsVideoVisitLinkForGuest(payload) {
    const start = new Date();
    const domain = yield select((state) => state.domain);
    try {
        if (domain.t) {
            const response = yield call(portal.smsVideoVisitLinkForGuest, domain.t, payload.room, payload.mobileNumber, payload.guestName);
            if (response && !response.hasError && !response.errorCode) {
                logAPI("Saga.VideoVisit.SMSVisitLinkForGuest.Success", start, null, response);
                yield put({ type: types.SMS_VIDEO_VISIT_LINK_FOR_GUEST_SUCCESS, response });
            }
            else {
                yield put({ type: types.SMS_VIDEO_VISIT_LINK_FOR_GUEST_FAILURE });
                logAPI("Saga.VideoVisit.SMSVisitLinkForGuest.Failure", start, null, { error: response && response.error, room: payload.room, phone: payload.mobileNumber, guestName: payload.guestName });
            }
        } else {
            yield put({ type: types.SMS_VIDEO_VISIT_LINK_FOR_GUEST_FAILURE });
            logAPI("Saga.VideoVisit.SMSVisitLinkForGuest.Failure", start, null);
        }
    } catch (error) {
        yield put({ type: types.SMS_VIDEO_VISIT_LINK_FOR_GUEST_FAILURE, error });
        logAPI("Saga.VideoVisit.SMSVisitLinkForGuest.Failure", start, null, JSON.stringify(error));
    }
    if (payload.callback) {
        payload.callback();
    }
}

export function* handleEmailVideoVisitLinkForGuest(payload) {
    const start = new Date();
    const domain = yield select((state) => state.domain);
    try {
        if (domain.t) {
            const response = yield call(portal.emailVideoVisitLinkForGuest, domain.t, payload.room, payload.email, payload.guestName);
            if (response && !response.hasError && !response.errorCode) {
                logAPI("Saga.VideoVisit.EmailVisitLinkForGuest.Success", start, null, response);
                yield put({ type: types.EMAIL_VIDEO_VISIT_LINK_FOR_GUEST_SUCCESS });
            }
            else {
                yield put({ type: types.EMAIL_VIDEO_VISIT_LINK_FOR_GUEST_FAILURE, response });
                logAPI("Saga.VideoVisit.EmailVisitLinkForGuest.Failure", start, null, { error: response && response.error, room: payload.room, email: payload.email, guestName: payload.guestName });
            }
        } else {
            yield put({ type: types.EMAIL_VIDEO_VISIT_LINK_FOR_GUEST_FAILURE });
            logAPI("Saga.VideoVisit.EmailVisitLinkForGuest.Failure", start, null);
        }
    } catch (error) {
        yield put({ type: types.EMAIL_VIDEO_VISIT_LINK_FOR_GUEST_FAILURE, error });
        logAPI("Saga.VideoVisit.EmailVisitLinkForGuest.Failure", start, null, JSON.stringify(error));
    }
    if (payload.callback) {
        payload.callback();
    }
}

export function* handleCancelVideoVisitLink(payload) {
    const start = new Date();
    const domain = yield select((state) => state.domain);
    try {
        if (domain.t) {
            const response = yield call(portal.cancelVideoVisitLink, domain.t, payload.shareCode);
            if (response && response.status === "CANCELED") {
                logAPI("Saga.VideoVisit.CancelVisitLink.Success", start, null);
                yield put({ type: types.CANCEL_VIDEO_VISIT_LINK_SUCCESS });
            }
            else {
                yield put({ type: types.CANCEL_VIDEO_VISIT_LINK_FAILURE });
                logAPI("Saga.VideoVisit.CancelVisitLink.Failure", start, null, response);
            }
        } else {
            yield put({ type: types.CANCEL_VIDEO_VISIT_LINK_FAILURE });
            logAPI("Saga.VideoVisit.CancelVisitLink.Failure", start, null);
        }
    } catch (err) {
        yield put({ type: types.CANCEL_VIDEO_VISIT_LINK_FAILURE });
        logAPI("Saga.VideoVisit.CancelVisitLink.Failure", start, null, JSON.stringify(err));
    }
    if (payload.callback) {
        payload.callback();
    }
}

export function* handleShareLinkHistory(payload) {
    const start = new Date();
    const domain = yield select((state) => state.domain);
    try {
        if (domain.t && payload.room) {
            const response = yield call(portal.getShareLinkHistory, domain.t, payload.room);
            logAPI("Saga.VideoVisit.ShareLinkHistory.Success", start, null);
            yield put({ type: types.VIDEO_VISIT_SHARE_LINK_HISTORY_SUCCESS, list: response });
        } else {
            yield put({ type: types.VIDEO_VISIT_SHARE_LINK_HISTORY_FAILURE });
            logAPI("Saga.VideoVisit.ShareLinkHistory.Failure", start, null, { error: 'incomplete input data' });
        }
    } catch (err) {
        yield put({ type: types.VIDEO_VISIT_SHARE_LINK_HISTORY_FAILURE });
        logAPI("Saga.VideoVisit.ShareLinkHistory.Failure", start, null, JSON.stringify(err));
    }
}

export function* handleVideoVisitSurvey(payload) {
    const start = new Date();
    const domain = yield select((state) => state.domain);
    try {
        yield put({ type: types.AMBULATORY_VIDEO_VISIT_SURVEY_REQUEST_PENDING });
        if (domain.t) {
            const response = yield call(ambulatorySurveyRequest, domain.t, payload);
            if (response === 200) {
                logAPI("Saga.AmbulatorySurvey.Success", start, null);
                yield put({ type: types.AMBULATORY_VIDEO_VISIT_SURVEY_REQUEST_SUCCESS });
            }
            else {
                yield put({ type: types.AMBULATORY_VIDEO_VISIT_SURVEY_REQUEST_FAILURE });
                logAPI("Saga.AmbulatorySurvey.Failure", start, null, response);
            }
        } else {
            yield put({ type: types.AMBULATORY_VIDEO_VISIT_SURVEY_REQUEST_FAILURE });
            logAPI("Saga.AmbulatorySurvey.NoToken.Failure", start, null);
        }
    } catch (err) {
        yield put({ type: types.AMBULATORY_VIDEO_VISIT_SURVEY_REQUEST_FAILURE });
        logAPI("Saga.AmbulatorySurvey.Failure", start, null, JSON.stringify(err));
    }
}

export function* guestLinkApprovalStatus(payload) {
    const domain = yield select((state) => state.domain);
    if (domain.idt && payload && payload.data) {
        yield put({ type: types.SET_GUEST_LINK_APPROVAL_STATUS, data: payload.data });
    }
}

export function* handleGuestAllowDeny(payload) {
    const start = new Date();
    const isAllowGuest = payload && payload.type && payload.type === types.ALLOW_GUEST ? true : false;
    try {
        const domainData = yield select((state) => state.domain);
        if (domainData && payload.roomShareId) {
            yield call(portal.allowOrDenyGuest, domainData.t, payload.roomShareId, isAllowGuest);
            if (payload.callback) payload.callback();
            logAPI(`Saga.VideoVisit.Guest.${isAllowGuest ? "Allowed" : "Denied"}.Success`, start, null);
        }
        else {
            logAPI(`Saga.VideoVisit.Guest.${isAllowGuest ? "Allowed" : "Denied"}.Failure`, start, null, { error: 'incomplete input data' });
        }
    } catch (err) {
        logAPI(`Saga.VideoVisit.Guest.${isAllowGuest ? "Allowed" : "Denied"}.Failure`, start, null, JSON.stringify(err));
    }
}

export function* handleAppointmentHistory(payload) {
    const domain = yield select(state => state.domain);
    const videoVisit = yield select(state => state.videoVisit);
    const { idt = null, er = null } = domain || {};
    const { startDate, endDate, providerid, departmentid } = payload;
    let departmentEpicIDs = departmentid ? departmentid.split(',') : [];
    let providerEpicIds = providerid ? providerid.split(',') : [];

    if (er === 'provider' && providerEpicIds.length > 0) {
        const providerDictionary = {};
        let selectedList = [];
        const providerExternalId = providerEpicIds.length > 0 ? providerEpicIds[0] : null;
        providerEpicIds = [];
        if (videoVisit.providerInfo) {
            departmentEpicIDs.forEach(departmentInternalId => {
                providerDictionary[departmentInternalId] = true; // to help quickly look up entries of the providerInfo array and store them in selectedList (see below, at outside of this forEach) 
            });
            selectedList = videoVisit.providerInfo.filter(p => providerDictionary[p.id]);
        }
        for (let i = 0; i < selectedList.length; i++) {
            const departmentInternalId = selectedList[i].id;
            if (selectedList[i].providerInDeptStatus === 'Active') {
                providerEpicIds.push({ providerExternalId, departmentInternalId });
            }
            if (selectedList[i].genericProviders) {
                providerEpicIds = providerEpicIds.concat(selectedList[i].genericProviders.map(p => ({ providerExternalId: p.id, departmentInternalId })));
            }
            else {
                providerEpicIds.push({ providerExternalId, departmentInternalId }); // departments like on-the-fly which have no generic providers
            }
        }
    }
    else if (providerEpicIds.length > 0) {
        providerEpicIds = providerEpicIds.map(providerExternalId => ({ providerExternalId, departmentInternalId: departmentEpicIDs.length > 0 ? departmentEpicIDs[0] : null }));
    }

    yield put({ type: types.APPOINTMENT_HISTORY_PENDING });
    const response = yield Tools.safeCall(instaDocBFFAPI.getProviderAppointments, idt, providerEpicIds, departmentEpicIDs, startDate, endDate, true);
    let visits = [];
    if (response && response.result && response.result.rooms && response.result.rooms.length > 0) {
        visits = response.result.rooms.reduce((acc, room) => {
            if (room.epicAppointmentDetails) {
                const { csnId, date, patientDOB, patientName, providers, startTime, startDateTime, completionDuration, departments, visitType } = room.epicAppointmentDetails;
                let providerName = "";
                let departmentName = "";
                let departmentInternalId = "";
                if (providers && providers.length > 0) {
                    const { name } = providers[0];
                    providerName = name ? name : "";
                }
                if (departments && departments.length > 0) {
                    const { name, internalId } = departments[0];
                    departmentName = name ? name : "";
                    departmentInternalId = internalId ? internalId : "";
                }
                acc.push({
                    csnId,
                    dateOfAppt: date,
                    startDateTime,
                    dateOfBirth: patientDOB,
                    patientName,
                    providerName,
                    apptTime: startTime,
                    duration: completionDuration,
                    departmentName,
                    departmentInternalId,
                    visitTypeInternalId: visitType && visitType.internalId ? visitType.internalId : ""
                });
            }
            return acc;

        }, []);
    }
    yield put({ type: types.APPOINTMENT_HISTORY_SUCCESS, visits });
}

export function* handleCreateVirtualRoom(payload) {
    const start = new Date();
    yield put({ type: types.CREATE_VIRTUAL_ROOM_PENDING });
    try {
        const domainData = yield select((state) => state.domain);
        const response = yield call(portal.createVirtualRoom, domainData.t, payload.payload);
        yield put({ type: types.CREATE_VIRTUAL_ROOM_SUCCESS, room: response });
        logAPI(`Saga.OnTheFlyVisitModal.Success`, start, null);

    } catch (err) {
        yield put({ type: types.CREATE_VIRTUAL_ROOM_FAILURE });
        logAPI(`Saga.OnTheFlyVisitModal.Failure`, start, null, JSON.stringify(err));
    }
}